import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';

import {
  CancelPushBroadcastMutation,
  CancelPushBroadcastMutationVariables,
  SendPushBroadcastNowMutation,
  SendPushBroadcastNowMutationVariables,
  UnschedulePushBroadcastMutation,
  UnschedulePushBroadcastMutationVariables,
} from '../../../../../../__gqltypes__';
import { CANCEL_PUSH_BROADCAST, SEND_PUSH_BROADCAST_NOW, UNSCHEDULE_PUSH_BROADCAST } from '../graphql';
import {
  formatPushUtc,
  pushBroadcastErrorIssues,
  PushBroadcastData,
  PushIssue,
  PushPerson,
  pushPersonLabel,
  pushRelativeTime,
} from '../utils';
import { PushIssueList } from './PushIssueList';
import { PUSH_CANCEL_CONFIRM } from './PushProgress';

type Props = {
  broadcast: PushBroadcastData;
  people: ReadonlyMap<string, PushPerson>;
  onChanged: (broadcast: PushBroadcastData) => void;
};

/**
 * A scheduled broadcast: when it fires (UTC), and the three ways out: back to draft to edit it, send now instead,
 * cancel. A lost Cloud Task is silent, so an overdue schedule is flagged right next to "Send now instead".
 */
export function PushSchedulePanel({ broadcast, people, onChanged }: Props): JSX.Element {
  const [issues, setIssues] = useState<PushIssue[]>([]);
  const [unschedule, { loading: unscheduling }] = useMutation<
    UnschedulePushBroadcastMutation,
    UnschedulePushBroadcastMutationVariables
  >(UNSCHEDULE_PUSH_BROADCAST);
  const [sendNow, { loading: sending }] = useMutation<
    SendPushBroadcastNowMutation,
    SendPushBroadcastNowMutationVariables
  >(SEND_PUSH_BROADCAST_NOW);
  const [cancel, { loading: cancelling }] = useMutation<
    CancelPushBroadcastMutation,
    CancelPushBroadcastMutationVariables
  >(CANCEL_PUSH_BROADCAST);
  const busy = unscheduling || sending || cancelling;

  const run = async (action: () => Promise<PushBroadcastData | undefined>) => {
    setIssues([]);
    try {
      const updated = await action();
      if (updated) {
        onChanged(updated);
      }
    } catch (error) {
      setIssues(pushBroadcastErrorIssues(error));
    }
  };

  const onUnschedule = () =>
    run(async () => (await unschedule({ variables: { id: broadcast.id } })).data?.unschedulePushBroadcast);

  const onSendNow = () => {
    const question = broadcast.isScheduleOverdue
      ? `Send "${broadcast.name}" now? Its scheduled time passed and it never started.`
      : `Send "${broadcast.name}" now instead of ${formatPushUtc(broadcast.sendAt)}?`;
    if (!window.confirm(`${question}\n\nA sent push cannot be recalled.`)) {
      return undefined;
    }
    return run(
      async () =>
        (await sendNow({ variables: { id: broadcast.id, expectedVersion: broadcast.revision } })).data
          ?.sendPushBroadcastNow
    );
  };

  const onCancel = () => {
    if (!window.confirm(PUSH_CANCEL_CONFIRM)) {
      return undefined;
    }
    return run(async () => (await cancel({ variables: { id: broadcast.id } })).data?.cancelPushBroadcast);
  };

  return (
    <Card className={`mb-3 ${broadcast.isScheduleOverdue ? 'border-danger' : 'border-info'}`}>
      <Card.Header className="font-weight-bold">Scheduled</Card.Header>
      <Card.Body>
        <div className="h5 mb-0">{formatPushUtc(broadcast.sendAt)}</div>
        <div className="text-muted small mb-3">
          {broadcast.sendAt ? pushRelativeTime(broadcast.sendAt, Date.now()) : ''} · scheduled by{' '}
          {pushPersonLabel(broadcast.startedBy, people)}. Every check (texts, destination, audience) runs again when it
          fires.
        </div>

        {broadcast.isScheduleOverdue ? (
          <Alert variant="danger" className="py-2">
            <div className="font-weight-bold">Overdue: it never started</div>
            Its time passed and the send did not start: the scheduled task was lost. Nothing was sent. Use &quot;Send
            now instead&quot;, or take it back to draft.
          </Alert>
        ) : null}

        <div className="d-flex flex-wrap">
          <Button
            size="sm"
            variant={broadcast.isScheduleOverdue ? 'danger' : 'outline-danger'}
            className="mr-2 mb-1"
            disabled={busy}
            onClick={onSendNow}
          >
            {sending ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
            Send now instead
          </Button>
          <Button size="sm" variant="outline-secondary" className="mr-2 mb-1" disabled={busy} onClick={onUnschedule}>
            {unscheduling ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
            Back to draft (to edit)
          </Button>
          <Button size="sm" variant="outline-dark" className="mb-1" disabled={busy} onClick={onCancel}>
            {cancelling ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
            Cancel
          </Button>
        </div>
        <PushIssueList issues={issues} title="The server refused" className="mt-2 mb-0" />
      </Card.Body>
    </Card>
  );
}
